import type { ReactNode } from "react";
import { Mono, Panel, Table, Td, Th } from "@/components/primitives";

/**
 * What CS can do in the Telegram group — for admins who brief the team.
 *
 * Static on purpose: every line restates behaviour the worker enforces
 * (spec §5–§6, decisions 3, 12, 30, 36–37, 50–51). Change the worker and this
 * page together, or it starts describing a different product.
 */

const COMMANDS: { command: string; who: string; what: ReactNode }[] = [
  {
    command: "/revoke",
    who: "Anyone, for their own messages. Group admins, for anyone's.",
    what: (
      <>
        Send it as a <b>reply</b> to the CS message you want to take back. It disappears from the
        player's chat and from the Topic, and the <Mono>/revoke</Mono> itself is removed. An album
        goes as a whole. Telegram allows this for 48 hours only.
      </>
    ),
  },
  {
    command: "/blacklist",
    who: "Group admins only.",
    what: (
      <>
        Bans the player whose Topic you are in. Nothing gets through in either direction, the Topic
        name gains a <Mono>[BAN]</Mono> prefix, and they stop receiving broadcasts and auto replies.
        Their messages are still kept as evidence.
      </>
    ),
  },
  {
    command: "/unblacklist",
    who: "Group admins only.",
    what: <>Lifts the ban: messages flow again and the prefix goes.</>,
  },
];

const NOTICES: { text: string; meaning: string }[] = [
  {
    text: "⚠️ Not delivered — please send again",
    meaning:
      "Your message could not reach the player for about a minute. Send it again; nothing was delivered.",
  },
  {
    text: "⚠️ Not delivered: player has blocked the bot",
    meaning:
      "They blocked the bot (or, with “account deleted”, left Telegram). Nothing you send will arrive.",
  },
  {
    text: "⛔ Player is banned — message not sent",
    meaning: "The player is on the blacklist. Use /unblacklist first if that was a mistake.",
  },
  {
    text: "✏️ … edited this message",
    meaning:
      "Someone edited a message — the player, or CS. The edit is applied on the other side too; the player is never told.",
  },
  {
    text: "🤖 Auto-replied: …",
    meaning:
      "The bot answered the player by itself with an auto reply. The text shows what they were told.",
  },
  {
    text: "📢 Received broadcast: …",
    meaning: "The player received a broadcast since their last message, so you know what they saw.",
  },
  {
    text: "📢 Replying to broadcast: …",
    meaning: "The player pressed Reply on a broadcast. The title says which one.",
  },
  {
    text: "↩️ … no copy in the player's chat",
    meaning:
      "You quoted a message the player does not have, so it arrived without the quote. Add context if needed.",
  },
  {
    text: "⚠️ Player sent a … that can't be displayed here",
    meaning: "A poll, dice, game or similar, which Telegram does not let a bot copy.",
  },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Panel title={title}>
      <div className="grid gap-3 px-5 py-4 leading-relaxed">{children}</div>
    </Panel>
  );
}

export function HelpPage() {
  return (
    <div className="grid max-w-4xl grid-cols-1 gap-4">
      <Section title="How the CS group works">
        <p>
          Every player has <b>one Topic</b> in the CS group, named <Mono>Source · #01 · Name</Mono>—
          or <Mono>Direct · #01 · Name</Mono> for someone who came to the bot directly. It opens the
          first time they type a message or press the <b>Ask our team</b> button, and it stays for
          good.
        </p>
        <p>
          <b>Whatever you post in a player's Topic goes to that player</b> — the Topic alone decides
          who receives it. The player sees it from the bot: never your name, never who on the team
          sent it. A message that arrives shows nothing; you only hear about the ones that fail.
        </p>
        <p>
          Messages in the <b>General</b> topic, or in a topic that is not a player's, go nowhere.
          Use them freely to talk among the team.
        </p>
      </Section>

      <Section title="What you can send">
        <p>
          Text, photos, videos, albums, files, voice messages, video notes, stickers, GIFs,
          locations and contacts all go through as they are.
        </p>
        <ul className="grid list-disc gap-1.5 pl-5">
          <li>
            <b>Edit</b> a message you sent and the player's copy changes with it — text, captions
            and replaced media.
          </li>
          <li>
            <b>Reply</b> to one of the player's messages to quote it; they see the quote on their
            side.
          </li>
          <li>
            Made a mistake? <Mono>/revoke</Mono> takes it back (below).
          </li>
        </ul>
      </Section>

      <Panel
        title="Commands"
        description="Typed inside a player's Topic. They are never sent to the player."
      >
        <Table>
          <thead>
            <tr>
              <Th className="w-36">Command</Th>
              <Th className="w-56">Who can use it</Th>
              <Th>What it does</Th>
            </tr>
          </thead>
          <tbody>
            {COMMANDS.map((c) => (
              <tr key={c.command}>
                <Td primary>
                  <Mono className="font-medium">{c.command}</Mono>
                </Td>
                <Td label="Who">{c.who}</Td>
                <Td label="What">{c.what}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <p className="border-t border-border px-5 py-3 text-muted-foreground">
          "Group admin" means an admin or the owner of the CS group in Telegram, checked at the
          moment you send the command — demote someone and they lose these straight away. The
          panel's own roles do not apply here.
        </p>
      </Panel>

      <Panel
        title="What the bot posts in a Topic"
        description="Only CS sees these, never the player."
      >
        <Table>
          <thead>
            <tr>
              <Th className="w-80">Notice</Th>
              <Th>What it means</Th>
            </tr>
          </thead>
          <tbody>
            {NOTICES.map((n) => (
              <tr key={n.text}>
                <Td primary className="[overflow-wrap:anywhere]">
                  {n.text}
                </Td>
                <Td label="Meaning">{n.meaning}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>

      <Section title="Good to know">
        <ul className="grid list-disc gap-1.5 pl-5">
          <li>
            A player's messages are never lost: if the group is briefly unavailable they are held
            and delivered when it is back.
          </li>
          <li>
            Never post a player's @username or Telegram ID in the group — the bot keeps them out on
            purpose.
          </li>
          <li>
            CS works only in Telegram. The panel is for admins: bots, players, broadcasts and
            automation.
          </li>
        </ul>
      </Section>
    </div>
  );
}
